import React, { useState } from 'react';
import { Modal, Typography, DatePicker, List, Button } from 'antd';
import classes from './Other.module.css';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { RightOutlined } from '@ant-design/icons';
import { AppDispatch } from 'shared/config/store/store';
import { RoundContainer } from 'shared/ui';
import { FileFormat } from 'shared/model/types';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';
import dayjs, { Dayjs } from 'dayjs';

interface OtherBlockProps {
  onStatementDownload?: ({
    dateRange,
    format
  }: {
    dateRange: Array<string>;
    format: 'pdf' | 'csv';
  }) => void | Promise<any>;
}
const OtherBlock: React.FC<OtherBlockProps> = ({ onStatementDownload }) => {
  const { formatMessage: t } = useIntl();
  const [statementModalVisible, setStatementModalVisible] = useState(false);
  const [dateRange, setDateRange] = useState<Array<string>>([]);

  const { showSuccess } = useMessage();

  const dispatch = useDispatch<AppDispatch>();

  const closeStatementModal = () => setStatementModalVisible(false);

  const downloadStatement = async (selectedFormat: FileFormat) => {
    if (onStatementDownload && dateRange.length === 2) {
      showSuccess(t({ id: 'river.views.statement.downloading' }), 'toast__download-in-progress');
      dispatch({ type: 'App/SHOW_LOADER' });
      await onStatementDownload({ dateRange, format: selectedFormat });
      setDateRange([]);
      dispatch({ type: 'App/HIDE_LOADER' });
      closeStatementModal();
    }
  };

  const handleDownload = async (selectedFormat: FileFormat) => {
    await downloadStatement(selectedFormat);
  };

  const isDateRangeValid = dateRange.length === 2;

  return (
    <div>
      <Typography.Text className={classes.title}>{t({ id: 'account.other' })}</Typography.Text>
      <List>
        <List.Item
          actions={[
            <RoundContainer
              size={32}
              id='other__statement'
              key='show-statement-action'
              onClick={() => setStatementModalVisible(true)}
              color='transparent'
              pointer
            >
              <RightOutlined style={{ fontSize: 16, color: 'rgba(0,0,0,0.6)' }} />
            </RoundContainer>
          ]}
        >
          <List.Item.Meta title={t({ id: 'account.statement' })} />
        </List.Item>
      </List>

      <Modal
        title={t({ id: 'account.statement' })}
        open={statementModalVisible}
        onCancel={closeStatementModal}
        footer={[]}
        style={{ height: 900 }}
        destroyOnClose
      >
        <Typography.Paragraph>{t({ id: 'account.statement.description' })}</Typography.Paragraph>
        <DatePicker.RangePicker
          style={{ height: 58, marginBottom: 20 }}
          getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
          onChange={(_, dates) => setDateRange(dates)}
          size='large'
          placement='bottomRight'
          className='w100 input__statement-date'
          disabledDate={(current: Dayjs) => current && current.isAfter(dayjs())}
        />
        <>
          <Button
            id='btn__statement-download-pdf'
            onClick={() => handleDownload(FileFormat.PDF)}
            type='primary'
            block
            disabled={!isDateRangeValid}
            style={{ marginBottom: '5px' }}
          >
            {t({ id: 'buttons.download' })} PDF
          </Button>

          <Button
            id='btn__statement-download-csv'
            onClick={() => handleDownload(FileFormat.CSV)}
            type='primary'
            block
            disabled={!isDateRangeValid}
          >
            {t({ id: 'buttons.download' })} CSV
          </Button>
        </>
      </Modal>
    </div>
  );
};

export default React.memo(OtherBlock);
